// Auto-remind (Oct 20, 2026): the opt-in balance reminders, homeowner-comms touch T8.
//
// Greg (Oct 15): no automated past-due reminders by default. Shannon turns them on for one client
// once she knows they are not waiting on an insurance check: the switch under the Send Invoice
// panel's delivery history writes deal_comm_settings, and the engine texts the pay link 7, 14 and 21
// days after the final invoice went out ("- Shannon, Peach Duck Contracting").
//
// The schedule and the stop rules live in the database (migration homeowner_comms_t8:
// hc_sweep_enqueue makes the reminder rows, hc_t8_stop_reason / hc_t8_check stop a cadence). This
// file mirrors the schedule so the switch can say what happens next; change both together.

import { addDaysISO, etDate } from "./weeklySpiffs";

export const T8_STEPS = [7, 14, 21] as const;

/** Reminders run only while the deal is in one of these stages. */
export const REMIND_STAGES = new Set<string>(["collecting", "delinquent"]);

/** After this many days the step-21 week is over (hc_t8_stop_reason's "expired"). */
export const ANCHOR_MAX_AGE_DAYS = 27;

/** Minimum days between two reminders to one homeowner. */
export const MIN_DAYS_BETWEEN = 4;

/** deal_comm_settings, the auto-remind half. */
export interface ReminderSettings {
  deal_id?: string;
  auto_remind: boolean;
  auto_remind_anchor_delivery_id: string | null;
  auto_remind_enabled_by: string | null;
  auto_remind_enabled_at: string | null;
  auto_remind_paused_at: string | null;
  auto_remind_stopped_at: string | null;
  auto_remind_stop_reason: string | null;
  mute_all?: boolean | null;
  updated_by?: string | null;
}

/** A deal_invoice_deliveries row. */
export interface ReminderDelivery {
  id: string;
  sent_at: string;
  supplement_approved_amount: number | string | null;
  revoked_at?: string | null;
  paid_at?: string | null;
}

/** A homeowner_comm_sends row with comm_key 't8' (ref = "<anchor id>:<step>", "#r<ms>" on a resend). */
export interface ReminderRow {
  ref: string;
  status: string;
  skip_reason: string | null;
  sent_at: string | null;
  scheduled_for?: string | null;
  created_at?: string | null;
}

export type AutoRemindKind = "off" | "on" | "paused" | "stopped";

export function autoRemindKind(s: ReminderSettings | null | undefined): AutoRemindKind {
  if (!s) return "off";
  if (s.auto_remind_stopped_at) return "stopped";
  if (!s.auto_remind) return "off";
  return s.auto_remind_paused_at ? "paused" : "on";
}

/**
 * A final invoice: one that went out once the carrier decided the supplement. It carries an approved
 * amount, or it is a retail job, or it went out on or after the day the supplement was decided (a
 * $0 approval still ends in a real final invoice).
 */
export function isApprovedDelivery(
  dl: ReminderDelivery,
  deal: { is_retail?: boolean | null; supplement_approved_date?: string | null },
): boolean {
  if (dl.revoked_at) return false;
  if (Number(dl.supplement_approved_amount ?? 0) > 0) return true;
  if (deal.is_retail) return true;
  return !!deal.supplement_approved_date && etDate(new Date(dl.sent_at)) >= deal.supplement_approved_date.slice(0, 10);
}

/** The invoice reminders follow: the newest final invoice. */
export function pickAnchor(
  deliveries: ReminderDelivery[],
  deal: { is_retail?: boolean | null; supplement_approved_date?: string | null },
): ReminderDelivery | null {
  let best: ReminderDelivery | null = null;
  for (const dl of deliveries) {
    if (!isApprovedDelivery(dl, deal)) continue;
    if (!best || Date.parse(dl.sent_at) > Date.parse(best.sent_at)) best = dl;
  }
  return best;
}

/** Days between two YYYY-MM-DD dates. */
export function daysBetween(fromISO: string, toISO: string): number {
  return Math.round((Date.parse(toISO + "T12:00:00Z") - Date.parse(fromISO + "T12:00:00Z")) / 86_400_000);
}

/** Eastern minutes past midnight. */
export function etMinutes(d: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return (get("hour") % 24) * 60 + get("minute");
}

export function stepDueDate(anchorSentAt: string, step: number): string {
  return addDaysISO(etDate(new Date(anchorSentAt)), step);
}

function stepOf(ref: string): number | null {
  const m = ref.split("#")[0].match(/:(\d+)$/);
  return m ? Number(m[1]) : null;
}

function anchorOf(ref: string): string {
  return ref.split("#")[0].split(":")[0].toLowerCase();
}

const SENT = new Set(["sent", "partial"]);
const isSunday = (iso: string) => new Date(iso + "T12:00:00Z").getUTCDay() === 0;

/** The reminder rows of one anchor, the newest row per step (a resend replaces the original). */
export function rowsByStep(rows: ReminderRow[], anchorId: string): Map<number, ReminderRow> {
  const out = new Map<number, ReminderRow>();
  for (const r of rows) {
    if (anchorOf(r.ref) !== anchorId.toLowerCase()) continue;
    const step = stepOf(r.ref);
    if (step == null) continue;
    const prev = out.get(step);
    if (!prev || Date.parse(r.created_at ?? r.sent_at ?? "") > Date.parse(prev.created_at ?? prev.sent_at ?? "")) out.set(step, r);
  }
  return out;
}

/** The Eastern date of the deal's newest reminder that went out (any anchor), or null. */
export function lastSentDate(rows: ReminderRow[]): string | null {
  let best: string | null = null;
  for (const r of rows) {
    if (!SENT.has(r.status) || !r.sent_at) continue;
    const d = etDate(new Date(r.sent_at));
    if (!best || d > best) best = d;
  }
  return best;
}

/**
 * The days the remaining reminders would go out if nothing changes: the sweep's rule played forward
 * one day at a time. Each day, 10 AM to 6 PM except Sundays, the latest step that is due gets its row
 * if it has none, it is still in its own week, and the last reminder was at least 4 days earlier.
 */
export function projectSchedule(
  anchorSentAt: string,
  rows: ReminderRow[],
  anchorId: string,
  now: Date = new Date(),
): Array<{ step: number; date: string }> {
  const byStep = rowsByStep(rows, anchorId);
  const done = new Set<number>(byStep.keys());
  let last = lastSentDate(rows);
  const today = etDate(now);
  const out: Array<{ step: number; date: string }> = [];
  for (let i = 0; i <= 40; i++) {
    const day = addDaysISO(today, i);
    if (i === 0 && etMinutes(now) >= 18 * 60) continue;
    if (isSunday(day)) continue;
    let latest: number | null = null;
    for (const step of T8_STEPS) if (stepDueDate(anchorSentAt, step) <= day) latest = step;
    if (latest == null || done.has(latest)) continue;
    if (daysBetween(stepDueDate(anchorSentAt, latest), day) >= 7) continue;
    if (last && daysBetween(last, day) < MIN_DAYS_BETWEEN) continue;
    out.push({ step: latest, date: day });
    done.add(latest);
    last = day;
  }
  return out;
}

export type StepStatus = "sent" | "skipped" | "failed" | "waiting" | "held" | "shadow" | "upcoming" | "missed";

export interface StepView {
  step: number;
  due: string;
  status: StepStatus;
  /** when it went (sent) or will go (upcoming / waiting) */
  date: string | null;
  reason: string | null;
}

/** One line per step for the switch: what happened, or when it goes. */
export function reminderSteps(
  anchorSentAt: string,
  rows: ReminderRow[],
  anchorId: string,
  now: Date = new Date(),
  running = true,
): StepView[] {
  const byStep = rowsByStep(rows, anchorId);
  const projected = running ? projectSchedule(anchorSentAt, rows, anchorId, now) : [];
  return T8_STEPS.map((step) => {
    const due = stepDueDate(anchorSentAt, step);
    const r = byStep.get(step);
    if (r) {
      const status: StepStatus = SENT.has(r.status) ? "sent"
        : r.status === "skipped" ? "skipped"
        : r.status === "failed" ? "failed"
        : r.status === "held" ? "held"
        : r.status === "shadow" ? "shadow"
        : "waiting";
      const when = r.sent_at ?? r.scheduled_for ?? null;
      return { step, due, status, date: when ? etDate(new Date(when)) : null, reason: r.skip_reason };
    }
    const p = projected.find((x) => x.step === step);
    if (p) return { step, due, status: "upcoming", date: p.date, reason: null };
    // Running, the sweep will pass over it (a later step came due first). Paused or off, it still can
    // go while its week lasts, on a day nobody can know yet.
    if (!running && daysBetween(due, etDate(now)) < 7) return { step, due, status: "upcoming", date: null, reason: null };
    return { step, due, status: "missed", date: null, reason: null };
  });
}

/** The next reminder while the switch is on, or null. */
export function nextReminder(
  settings: ReminderSettings | null | undefined,
  anchorSentAt: string | null,
  rows: ReminderRow[],
  now: Date = new Date(),
): { step: number; date: string } | null {
  if (autoRemindKind(settings) !== "on" || !anchorSentAt || !settings?.auto_remind_anchor_delivery_id) return null;
  return projectSchedule(anchorSentAt, rows, settings.auto_remind_anchor_delivery_id, now)[0] ?? null;
}

/** Why the switch can't be turned on, decided in the portal (the server adds the money rules). */
export function enableBlock(
  stage: string | null | undefined,
  anchor: ReminderDelivery | null,
  now: Date = new Date(),
): string | null {
  if (!REMIND_STAGES.has(stage ?? "")) return "Reminders run while the deal is in Collecting or Delinquent.";
  if (!anchor) {
    return "Send the final invoice first (once the carrier has decided the supplement). Reminders follow it by 7, 14 and 21 days.";
  }
  const age = daysBetween(etDate(new Date(anchor.sent_at)), etDate(now));
  if (age > ANCHOR_MAX_AGE_DAYS) {
    return `The last final invoice went out ${fmtDay(etDate(new Date(anchor.sent_at)))}, ${age} days ago. Reminders follow an invoice by 7, 14 and 21 days, so send an updated invoice first.`;
  }
  return null;
}

/** "Tue, Oct 20" for a YYYY-MM-DD date. */
export function fmtDay(iso: string): string {
  return new Date(iso.slice(0, 10) + "T12:00:00Z").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
  });
}

/** "today", "tomorrow" or "Mon, Oct 26". */
export function fmtWhen(iso: string, now: Date = new Date()): string {
  const today = etDate(now);
  if (iso === today) return "today";
  if (iso === addDaysISO(today, 1)) return "tomorrow";
  return fmtDay(iso);
}

const STOP_WORDS: Record<string, string> = {
  stopped_by_staff: "turned off",
  no_anchor: "the invoice it followed is gone",
  anchor_revoked: "the invoice link was revoked",
  anchor_paid: "they paid through the invoice link",
  stage_changed: "the deal left Collecting",
  paid: "the balance is paid",
  payment_logged: "a payment was logged",
  check_in_flight: "a check is being processed",
  opted_out: "the homeowner replied STOP",
  muted: "automatic texts are muted on this deal",
  homeowner_waiting: "they replied WAITING (still waiting on the insurance check)",
  completed: "all three reminders have gone",
  expired: "the 21-day reminder window ended",
};

/** Plain words for why a cadence stopped. */
export function stopReasonText(reason: string | null | undefined): string {
  if (!reason) return "stopped";
  return STOP_WORDS[reason] ?? reason.replace(/_/g, " ");
}

const SKIP_WORDS: Record<string, string> = {
  staff_texted: "someone on the team texted them in the last 3 days",
  homeowner_replied: "they texted in the last 3 days",
  invoice_sent: "an invoice went out in the last 3 days",
  text_scheduled: "a text to them was already scheduled",
  invoice_stale: "the invoice amount changed; send an updated invoice",
  reminders_stopped: "reminders were stopped",
  reminders_off: "Auto-remind was off",
  paused: "Auto-remind was paused",
  anchor_changed: "a newer invoice took over",
  no_invoice_link: "there was no invoice link",
  no_balance: "there was no balance on file",
  disabled: "reminders are switched off for everyone",
  muted: "automatic texts are muted on this deal",
  opted_out: "the homeowner replied STOP",
  no_contact: "there is no phone on file",
  no_channel: "there is no valid phone on file",
  deal_closed: "the deal is closed",
};

/** Plain words for why one reminder was skipped (the cadence carried on). */
export function skipReasonText(reason: string | null | undefined): string {
  if (!reason) return "skipped";
  return SKIP_WORDS[reason] ?? STOP_WORDS[reason] ?? reason.replace(/_/g, " ");
}
